import { useCallback, useEffect, useRef, useState } from 'react'
import { ALGORITHMS } from './algorithms'
import { ensureAudioContext, playTone, valueToFrequency } from './sound'

const randomArray = (size) =>
  Array.from({ length: size }, () => Math.floor(Math.random() * 96) + 5)

export function useSortingVisualizer() {
  const [array, setArray] = useState(() => randomArray(30))
  const [size, setSize] = useState(30)
  const [speed, setSpeed] = useState(50)
  const [algorithmKey, setAlgorithmKey] = useState(ALGORITHMS[0].key)
  const [comparing, setComparing] = useState([])
  const [swapping, setSwapping] = useState([])
  const [sortedIndices, setSortedIndices] = useState(() => new Set())
  const [isSorting, setIsSorting] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const [currentLine, setCurrentLine] = useState(null)
  const [soundOn, setSoundOn] = useState(true)

  const initialRef = useRef(array)
  const workingRef = useRef(array)
  const generatorRef = useRef(null)
  const intervalRef = useRef(null)
  const speedRef = useRef(speed)
  const soundOnRef = useRef(soundOn)
  speedRef.current = speed
  soundOnRef.current = soundOn

  const stopTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const playSoundForOp = useCallback((op) => {
    if (!soundOnRef.current) return

    switch (op.type) {
      case 'compare': {
        const values = op.indices.map((index) => workingRef.current[index])
        const [low, high] = [...values].sort((a, b) => a - b)
        playTone({
          frequency: valueToFrequency(low),
          duration: 0.04,
          type: 'sine',
          volume: 0.035,
        })
        playTone({
          frequency: valueToFrequency(high),
          duration: 0.04,
          type: 'sine',
          volume: 0.035,
          delay: 0.03,
        })
        break
      }
      case 'swap':
        playTone({
          frequency: valueToFrequency(workingRef.current[op.indices[0]]),
          duration: 0.09,
          type: 'sine',
          volume: 0.07,
        })
        break
      case 'overwrite':
        playTone({
          frequency: valueToFrequency(op.value),
          duration: 0.08,
          type: 'sine',
          volume: 0.06,
        })
        break
      case 'sorted':
        playTone({
          frequency: valueToFrequency(workingRef.current[op.index]),
          duration: 0.14,
          type: 'triangle',
          volume: 0.07,
        })
        break
      default:
        break
    }
  }, [])

  const tick = useCallback((opsOverride) => {
    const generator = generatorRef.current
    if (!generator) return

    const opsPerTick = opsOverride ?? Math.max(1, Math.ceil(speedRef.current / 25))
    let finished = false
    let changed = false

    for (let i = 0; i < opsPerTick; i++) {
      const { value: op, done } = generator.next()
      if (done) {
        finished = true
        break
      }
      changed = true
      setCurrentLine(typeof op.line === 'number' ? op.line : null)

      switch (op.type) {
        case 'compare':
          setSwapping([])
          setComparing(op.indices)
          break
        case 'swap': {
          const [x, y] = op.indices
          const working = workingRef.current
          ;[working[x], working[y]] = [working[y], working[x]]
          setComparing([])
          setSwapping(op.indices)
          break
        }
        case 'overwrite': {
          workingRef.current[op.index] = op.value
          setComparing([])
          setSwapping([op.index])
          break
        }
        case 'sorted':
          setComparing([])
          setSwapping([])
          setSortedIndices((prev) => {
            const next = new Set(prev)
            next.add(op.index)
            return next
          })
          break
        default:
          break
      }

      playSoundForOp(op)
    }

    if (changed) {
      setArray([...workingRef.current])
    }

    if (finished) {
      stopTimer()
      setArray([...workingRef.current])
      setComparing([])
      setCurrentLine(null)
      setSwapping([])
      setIsSorting(false)
      setIsDone(true)
      generatorRef.current = null
    }
  }, [stopTimer, playSoundForOp])

  const prepare = useCallback((key = algorithmKey) => {
    const algorithm = ALGORITHMS.find((a) => a.key === key)
    if (!algorithm) return false

    const base = [...initialRef.current]
    workingRef.current = [...base]
    setArray(base)
    setComparing([])
    setSwapping([])
    setCurrentLine(null)
    setSortedIndices(new Set())
    setIsDone(false)
    generatorRef.current = algorithm.fn(workingRef.current)
    return true
  }, [algorithmKey])

  useEffect(() => {
    if (!isSorting) return
    const delay = Math.max(1, 110 - speed)
    intervalRef.current = setInterval(tick, delay)
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isSorting, speed, tick])

  useEffect(() => stopTimer, [stopTimer])

  const newArray = useCallback((newSize = size) => {
    stopTimer()
    setIsSorting(false)
    generatorRef.current = null
    const next = randomArray(newSize)
    initialRef.current = [...next]
    workingRef.current = [...next]
    setSize(newSize)
    setArray(next)
    setComparing([])
    setSwapping([])
    setCurrentLine(null)
    setSortedIndices(new Set())
    setIsDone(false)
  }, [size, stopTimer])

  const start = useCallback(() => {
    if (isSorting) return
    ensureAudioContext()
    if (!generatorRef.current && !prepare()) return
    setIsSorting(true)
  }, [isSorting, prepare])

  const pause = useCallback(() => {
    stopTimer()
    setIsSorting(false)
  }, [stopTimer])

  const step = useCallback(() => {
    stopTimer()
    setIsSorting(false)
    ensureAudioContext()
    if (!generatorRef.current && !prepare()) return
    tick(1)
  }, [stopTimer, prepare, tick])

  const stop = useCallback(() => {
    stopTimer()
    setIsSorting(false)
    generatorRef.current = null
    const base = [...initialRef.current]
    workingRef.current = [...base]
    setArray(base)
    setComparing([])
    setCurrentLine(null)
    setSwapping([])
    setSortedIndices(new Set())
    setIsDone(false)
  }, [stopTimer])

  const setAlgorithm = useCallback((key) => {
    stopTimer()
    setIsSorting(false)
    setAlgorithmKey(key)
    prepare(key)
  }, [stopTimer, prepare])

  const toggleSound = useCallback(() => {
    if (!soundOnRef.current) {
      ensureAudioContext()
    }
    setSoundOn((prev) => !prev)
  }, [])

  return {
    array,
    size,
    speed,
    algorithmKey,
    comparing,
    swapping,
    sortedIndices,
    currentLine,
    isSorting,
    isDone,
    soundOn,
    newArray,
    start,
    pause,
    step,
    stop,
    setAlgorithm,
    setSpeed,
    toggleSound,
  }
}
